import { computed, inject, signal, Service } from '@angular/core';
import { tap } from 'rxjs/operators';
import { Api } from '@api/api';
import {
  confirmPhoneNumberStep1,
  confirmPhoneNumberStep2,
  getAccount,
  getCustomer,
  getCustomerOrders,
  getLastOrderedShipmentAddress,
  mergeAccountsConfirm,
  mergeAccountsInit,
  updateEmailStep1,
  updateEmailStep2,
  updateFullName,
  updatePhoneNumberStep1,
  updatePhoneNumberStep2,
} from '@api/functions';
import type {
  CustomerDto,
  MergeAccountsConfirmDto,
  MergeAccountsInitDto,
  OtpConfirmDto,
  UpdateEmailDto,
  UpdateFullNameDto,
  UpdatePhoneNumberRequestDto,
} from '@api/models';

@Service()
export class CustomerService {
  private api = inject(Api);

  customer = signal<CustomerDto>(null);
  isLoggedIn = computed<boolean>(() => !!this.customer());
  customerName = computed<string>(() => this.customer()?.contactInfo.firstName || '');

  fetchAndSetCustomer() {
    return this.api.invoke(getCustomer)
      .pipe(
        tap(response => this.setCustomer(response)),
      );
  }

  setCustomer(customer: CustomerDto) {
    this.customer.set(customer);
  }

  updateFullName(dto: UpdateFullNameDto) {
    return this.api.invoke(updateFullName, { body: dto })
      .pipe(
        tap(response => this.setCustomer(response)),
      );
  }

  updatePhoneNumberInit(dto: UpdatePhoneNumberRequestDto) {
    return this.api.invoke(updatePhoneNumberStep1, { body: dto })
      .pipe(
        tap(response => {
          if (response.customer) {
            this.setCustomer(response.customer);
          }
        }),
      );
  }

  updatePhoneNumberConfirm(dto: OtpConfirmDto) {
    return this.api.invoke(updatePhoneNumberStep2, { body: dto })
      .pipe(
        tap(response => this.setCustomer(response)),
      );
  }

  updateEmailInit(dto: UpdateEmailDto) {
    return this.api.invoke(updateEmailStep1, { body: dto });
  }

  updateEmailConfirm(dto: OtpConfirmDto) {
    return this.api.invoke(updateEmailStep2, { body: dto })
      .pipe(
        tap(response => this.setCustomer(response)),
      );
  }

  /**
   * Step 1 of absorbing the account that holds a conflicting identifier into the current one: sends an
   * OTP to that account and returns a preview of what the merge would do.
   */
  mergeAccountsInit(dto: MergeAccountsInitDto) {
    return this.api.invoke(mergeAccountsInit, { body: dto });
  }

  /** Step 2: the merge itself. The session survives, so the customer stays exactly where they were. */
  mergeAccountsConfirm(dto: MergeAccountsConfirmDto) {
    return this.api.invoke(mergeAccountsConfirm, { body: dto })
      .pipe(
        tap(response => this.setCustomer(response)),
      );
  }

  confirmPhoneNumberInit() {
    return this.api.invoke(confirmPhoneNumberStep1);
  }

  confirmPhoneNumberConfirm(dto: OtpConfirmDto) {
    return this.api.invoke(confirmPhoneNumberStep2, { body: dto })
      .pipe(
        tap(response => this.setCustomer(response)),
      );
  }

  fetchOrders() {
    return this.api.invoke(getCustomerOrders);
  }

  fetchLastOrderedShipmentAddress() {
    return this.api.invoke(getLastOrderedShipmentAddress);
  }

  fetchCustomerDetails() {
    return this.api.invoke(getAccount);
  }
}
